import { formatPercent, formatUSD } from '@/lib/format-utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { CeoTable } from './ceo-table'

const percent = (value) => formatPercent(value, 0, 1)
const money = (value) => formatUSD(value, 0, 0)

// 与业务群表同规则：回本线 100%，±3 点为预测误差带（CEO 2026-10-26）
const d90Tone = (value) => {
  if (value == null) return ''
  if (value < 0.97) return 'red'
  if (value <= 1.03) return 'yellow'
  return ''
}

const rowTone = (row) => {
  const tone = d90Tone(row.d90)
  return cn(
    tone === 'red' && 'bg-destructive/10 hover:bg-destructive/15',
    tone === 'yellow' && 'bg-warning/10 hover:bg-warning/15',
  )
}

const gapText = (gap) => {
  if (gap == null) return '-'
  const points = gap * 100
  return points > 0 ? `差 ${points.toFixed(1)}` : `超 ${(-points).toFixed(1)}`
}

const bestText = (best) => (best ? `${best.month} · ${money(best.dailyProfit180)} / 天` : '-')

const COLUMNS = [
  '日均花费（近 28 天）',
  '花费占比',
  'D7 实测',
  'D30 实测',
  '预测 D90',
  '预测 D180',
  '回本日',
  'D180 贡献毛利 / 天',
  '可达线 D180',
  '距可达线（点）',
  '历史最好月',
]

function Cells({ row, share, attainableGap, best }) {
  const tone = d90Tone(row.d90)
  return (
    <>
      <TableCell className="text-right tabular-nums">{money(row.dailyCost)}</TableCell>
      <TableCell className="text-right tabular-nums">{percent(share)}</TableCell>
      <TableCell className="text-right tabular-nums">{percent(row.d7)}</TableCell>
      <TableCell className="text-right tabular-nums">{percent(row.d30)}</TableCell>
      <TableCell
        className={cn(
          'text-right font-medium tabular-nums',
          tone === 'red' && 'text-destructive',
          tone === 'yellow' && 'text-warning',
        )}
      >
        {percent(row.d90)}
      </TableCell>
      <TableCell className="text-right tabular-nums">{percent(row.d180)}</TableCell>
      <TableCell className="text-right tabular-nums">
        {row.paybackDay == null ? '>180' : `D${row.paybackDay}`}
      </TableCell>
      <TableCell className="text-right tabular-nums">{money(row.profit180)}</TableCell>
      <TableCell className="text-right tabular-nums">{percent(row.attainableD180)}</TableCell>
      <TableCell className="text-right tabular-nums">{gapText(attainableGap)}</TableCell>
      <TableCell className="whitespace-nowrap text-right tabular-nums">{bestText(best)}</TableCell>
    </>
  )
}

/** MON-59 产品对比：当前范围下每个 pid，表底合计行 = 当前范围；onSelect(id) 收窄到该 pid（MON-93） */
export function PidTable({ rows, total, totalLabel, onSelect }) {
  const totalGap = total.attainableD180 == null || total.d180 == null ? null : total.attainableD180 - total.d180
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">产品对比</CardTitle>
      </CardHeader>
      <CardContent>
        <CeoTable>
          <TableHeader className="bg-muted">
            <TableRow>
              <TableHead>pid</TableHead>
              {COLUMNS.map((column) => (
                <TableHead key={column} className="whitespace-nowrap text-right">{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(rowTone(row), onSelect && 'cursor-pointer')}
                onClick={onSelect && (() => onSelect(row.id))}
              >
                <TableCell className="whitespace-nowrap font-medium">
                  {row.name}
                  <span className="ml-1 text-xs font-normal text-muted-foreground">{row.pid}</span>
                </TableCell>
                <Cells row={row} share={row.share} attainableGap={row.attainableGap} best={row.best} />
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow className={rowTone(total)}>
              <TableCell className="whitespace-nowrap font-medium">合计（{totalLabel}）</TableCell>
              <Cells row={total} share={1} attainableGap={totalGap} best={null} />
            </TableRow>
          </TableFooter>
        </CeoTable>
      </CardContent>
    </Card>
  )
}
